import { useState, useEffect, Fragment } from 'react'
import { Menu, Transition } from '@headlessui/react'
import { Bell, Check, X, Mail, Users, Calendar, ExternalLink } from 'lucide-react'
import { apiClient } from '@/lib/api'

interface Notification {
  id: string  // Changed from number to string to support UUID
  title: string
  message: string
  type: string
  is_read: boolean
  read_at: string | null
  priority: string
  expires_at: string | null
  created_at: string
  created_by: string
  related_entity: {
    type: string
    id: number
  } | null
  action_data: any
}

export default function NotificationBell() {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    loadNotifications()
    // loadUnreadCount() // DISABLED

    // Poll for new notifications every 30 seconds
    // const interval = setInterval(() => {
    //   loadUnreadCount()
    // }, 30000)

    // return () => clearInterval(interval) // DISABLED
  }, [])

  const loadNotifications = async () => {
    try {
      setLoading(true)
      const response = await apiClient.getNotifications({ limit: 10 })
      
      if (response.success) {
        setNotifications((response.data as any[]) || [])
      }
    } catch (err) {
      setError('Failed to load notifications')
    } finally {
      setLoading(false)
    }
  }

  // const loadUnreadCount = async () => {
  //   try {
  //     const response = await apiClient.getUnreadNotificationCount()
  //     
  //     if (response.success) {
  //       setUnreadCount(response.data.unread_count)
  //     }
  //   } catch (err) {
  //     // Silently fail for count updates
  //   }
  // } // DISABLED

  const markAsRead = async (notificationId: string) => {
    try {
      const response = await apiClient.markNotificationAsRead(notificationId as any)
      
      if (response.success) {
        setNotifications(prev => prev.map(n => 
          n.id === notificationId ? { ...n, is_read: true, read_at: new Date().toISOString() } : n
        ))
        setUnreadCount(prev => Math.max(0, prev - 1))
      }
    } catch (err) {
      // Silently fail
    }
  }

  const markAllAsRead = async () => {
    try {
      const response = await apiClient.markAllNotificationsAsRead()
      
      if (response.success) {
        setNotifications(prev => prev.map(n => ({ ...n, is_read: true, read_at: new Date().toISOString() })))
        setUnreadCount(0)
      }
    } catch (err) {
      // Silently fail
    }
  }

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'team_invitation': return <Users className="w-4 h-4 text-blue-600" />
      case 'candidate_shared': return <Mail className="w-4 h-4 text-green-600" />
      case 'interview_scheduled': return <Calendar className="w-4 h-4 text-purple-600" />
      default: return <Bell className="w-4 h-4 text-gray-600" />
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'border-l-4 border-red-500 bg-red-50'
      case 'high': return 'border-l-4 border-orange-500 bg-orange-50'
      case 'normal': return 'border-l-4 border-blue-500 bg-blue-50'
      default: return 'border-l-4 border-gray-300 bg-gray-50'
    }
  }

  return (
    <Menu as="div" className="relative inline-block text-left">
      <div>
        <Menu.Button className="relative p-2 text-gray-600 hover:text-gray-900 focus:outline-none">
          <Bell className="w-6 h-6" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Menu.Button>
      </div>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 mt-2 w-80 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-900">Notifications</h3>
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  Mark all as read
                </button>
              )}
            </div>

            {loading ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
              </div>
            ) : error ? (
              <div className="text-center py-4 text-red-600 text-sm">
                {error}
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Bell className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                <p className="text-sm">No notifications</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {notifications.map((notification) => (
                  <Menu.Item key={notification.id}>
                    <div
                      className={`p-3 rounded-lg cursor-pointer hover:bg-gray-100 ${
                        !notification.is_read ? 'bg-blue-50 border-l-4 border-blue-500' : getPriorityColor(notification.priority)
                      }`}
                      onClick={() => !notification.is_read && markAsRead(notification.id)}
                    >
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 mt-0.5">
                          {getNotificationIcon(notification.type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {notification.title}
                            </p>
                            {!notification.is_read && (
                              <div className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0 ml-2"></div>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                            {notification.message}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(notification.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </Menu.Item>
                ))}
              </div>
            )}

            {notifications.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <button
                  onClick={() => {
                    // Navigate to full notifications page if you create one
                    console.log('Navigate to notifications page')
                  }}
                  className="w-full text-center text-sm text-blue-600 hover:text-blue-800"
                >
                  View all notifications
                </button>
              </div>
            )}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  )
}